import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

const breakpoints = { mobile: '768px', tablet: '1024px' };

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  padding: 1.25rem 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 1500px;

  @media (max-width: ${breakpoints.mobile}) { padding: 1rem; }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  font-size: 0.88rem;
  margin-bottom: 0.25rem;

  & svg.home { width: 16px; height: 16px; color: #6b7280; }
  & .sep { color: #cbd5e1; }
`;

const CrumbBtn = styled.button`
  background: none;
  border: none;
  color: ${({ active }) => (active ? '#1d4ed8' : '#6b7280')};
  font-weight: ${({ active }) => (active ? 700 : 500)};
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;

  &:hover { color: #1d4ed8; background: #eff6ff; }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1rem;
  align-items: start;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const CardHead = styled.div`
  padding: 0.95rem 1.15rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
`;

const CardBody = styled.div`
  padding: 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PathRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const PathLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
`;

const PathPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.85rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #111827;
  font-weight: 500;

  & svg { width: 16px; height: 16px; color: #fbbf24; }
`;

const DropZone = styled.div`
  border: 2px dashed ${({ active }) => (active ? '#2563eb' : '#cbd5e1')};
  background: ${({ active }) => (active ? '#eff6ff' : '#f9fafb')};
  border-radius: 12px;
  padding: 2.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.65rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #2563eb;
    background: #eff6ff;
  }
`;

const CloudUpIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.4rem;

  & svg { width: 38px; height: 38px; color: #2563eb; }
`;

const DropTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const DropSub = styled.div`
  font-size: 0.82rem;
  color: #6b7280;
  line-height: 1.55;
`;

const FilePickBtn = styled.button`
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.1rem;
  background: #2563eb;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { background: #1d4ed8; }

  & svg { width: 14px; height: 14px; }
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const InfoTile = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
`;

const InfoIconWrap = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  flex-shrink: 0;

  & svg { width: 14px; height: 14px; }
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const InfoLabel = styled.span`
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 0.82rem;
  color: #111827;
  font-weight: 600;
`;

const SectionTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.85rem;
  color: #374151;
  cursor: pointer;

  & input { accent-color: #2563eb; cursor: pointer; }
`;

const StartBtn = styled.button`
  width: 100%;
  padding: 0.85rem;
  background: ${({ disabled }) => (disabled ? '#bfdbfe' : '#2563eb')};
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 4px 10px rgba(37, 99, 235, 0.2)')};

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-top: 1px solid #f3f4f6;
  padding-top: 1rem;
`;

const FileItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.6rem 0.7rem;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  animation: ${fadeIn} 0.18s ease both;
`;

const FileIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 7px;
  background: ${({ bg }) => bg || '#e5e7eb'};
  color: ${({ color }) => color || '#374151'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const FileMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FileNameText = styled.span`
  font-size: 0.86rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSubText = styled.span`
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 1px;
`;

const ProgressBarOuter = styled.div`
  width: 100px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  width: ${({ pct }) => pct || 0}%;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  transition: width 0.2s ease;
`;

const RemoveBtn = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #fee2e2; color: #b91c1c; }

  & svg { width: 14px; height: 14px; }
`;

const TotalProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  color: #4b5563;
  margin-bottom: 0.4rem;
`;

// === 우측 패널 ===
const SideStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const GuideRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }

  & svg.bul { width: 18px; height: 18px; color: #2563eb; flex-shrink: 0; margin-top: 1px; }
`;

const GuideText = styled.div`
  font-size: 0.82rem;
  color: #4b5563;
  line-height: 1.5;
`;

const RecentRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
  &:first-child { padding-top: 0; }
`;

const RecentName = styled.span`
  font-size: 0.84rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RecentSub = styled.span`
  font-size: 0.7rem;
  color: #6b7280;
  margin-top: 1px;
`;

const CheckBadge = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #dcfce7;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;

  & svg { width: 12px; height: 12px; }
`;

const MoreLink = styled.button`
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;

const Empty = styled.div`
  text-align: center;
  padding: 1rem 0.5rem;
  color: #9ca3af;
  font-size: 0.82rem;
`;

const FooterText = styled.div`
  text-align: center;
  font-size: 0.72rem;
  color: #9ca3af;
  margin-top: 0.5rem;
`;

// === 아이콘 ===
const HomeSvg = () => (
  <svg className="home" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z" />
  </svg>
);

const FolderSvg = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" /></svg>
);

const CloudUpSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 16l-4-4-4 4" />
    <path d="M12 12v9" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    <path d="M16 16l-4-4-4 4" />
  </svg>
);

const FileSvgIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const FormatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);

const SizeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const StackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

const InfoCircleIcon = () => (
  <svg className="bul" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const BellSvg = () => (
  <svg className="bul" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const XSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function getFileTypeStyle(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return { bg: '#fef2f2', color: '#dc2626', label: 'PDF' };
  if (['doc', 'docx', 'hwp'].includes(ext)) return { bg: '#eff6ff', color: '#2563eb', label: 'DOC' };
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { bg: '#dcfce7', color: '#16a34a', label: 'XLS' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { bg: '#fef3c7', color: '#d97706', label: 'ZIP' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return { bg: '#f5f3ff', color: '#7c3aed', label: 'IMG' };
  return { bg: '#f3f4f6', color: '#6b7280', label: ext.slice(0, 3).toUpperCase() || 'FILE' };
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const RECENT_KEY = 'antsnest.recentUploads';
const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};
const saveRecent = (list) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 10))); } catch {}
};

const Upload = ({
  selectedRemote,
  drives,
  onRemoteChange,
  currentPath: externalCurrentPath,
  setCurrentPath: externalSetCurrentPath,
}) => {
  const [files, setFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [internalPath, setInternalPath] = useState('');
  const currentPath = externalCurrentPath !== undefined ? externalCurrentPath : internalPath;
  const setCurrentPath = externalSetCurrentPath || setInternalPath;
  const [overwrite, setOverwrite] = useState(true);
  const [notifyDone, setNotifyDone] = useState(false);
  const [openAfter, setOpenAfter] = useState(false);
  const [recent, setRecent] = useState(() => loadRecent());

  const handleDrag = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragActive(true);
    else if (e.type === 'dragleave') setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragActive(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...dropped]);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const sel = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...sel]);
  }, []);

  const openFileDialog = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = handleFileSelect;
    input.click();
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const uploadSingleFile = async (file) => {
    const url = selectedRemote.multipartsUrl;
    const formData = new FormData();
    formData.append('files', file);
    if (currentPath) formData.append('path', currentPath);

    const res = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadProgress((prev) => ({ ...prev, [file.name]: pct }));
        }
      },
    });
    if (res.status !== 200) throw new Error(`${file.name} 업로드 실패 (${res.status})`);
    return res.data;
  };

  const handleUpload = async () => {
    if (!selectedRemote) { alert('원격지를 선택하세요'); return; }
    if (files.length === 0) return;
    setIsUploading(true);
    try {
      for (const f of files) {
        await uploadSingleFile(f);
      }
      // 최근 업로드 기록
      const now = new Date();
      const dateStr = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const newRecent = [
        ...files.map((f) => ({ name: f.name, size: f.size, date: dateStr })),
        ...recent,
      ].slice(0, 10);
      setRecent(newRecent);
      saveRecent(newRecent);

      alert(`${files.length}개 파일 업로드 완료!`);
      setFiles([]);
      setUploadProgress({});
    } catch (err) {
      console.error(err);
      alert(`업로드 실패: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const totalProgress = (() => {
    const vals = Object.values(uploadProgress);
    if (vals.length === 0) return 0;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  })();

  const segs = (currentPath || '').split('/').filter(Boolean);

  return (
    <Page>
      <Breadcrumb>
        <CrumbBtn onClick={() => setCurrentPath('')}><HomeSvg /></CrumbBtn>
        {segs.length === 0 && <span className="sep">/</span>}
        {segs.map((seg, idx) => {
          const path = segs.slice(0, idx + 1).join('/');
          const isLast = idx === segs.length - 1;
          return (
            <React.Fragment key={path}>
              <span className="sep">/</span>
              <CrumbBtn active={isLast ? 1 : 0} onClick={() => setCurrentPath(path)}>{seg}</CrumbBtn>
            </React.Fragment>
          );
        })}
      </Breadcrumb>

      <Layout>
        {/* ==== 좌측 메인 업로드 카드 ==== */}
        <Card>
          <CardHead>
            <CardTitle>파일 업로드</CardTitle>
          </CardHead>
          <CardBody>
            <PathRow>
              <PathLabel>현재 경로</PathLabel>
              <PathPill>
                <FolderSvg />
                / {currentPath || ''}
              </PathPill>
            </PathRow>

            <DropZone
              active={isDragActive ? 1 : 0}
              onClick={openFileDialog}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <CloudUpIcon><CloudUpSvg /></CloudUpIcon>
              <DropTitle>파일을 드래그하거나 클릭하여 업로드하세요</DropTitle>
              <DropSub>
                여러 파일을 동시에 업로드할 수 있습니다.<br />
                최대 파일 크기: 5GB / 개
              </DropSub>
              <FilePickBtn type="button" onClick={(e) => { e.stopPropagation(); openFileDialog(); }}>
                <FileSvgIcon /> 파일 선택
              </FilePickBtn>
            </DropZone>

            <InfoRow>
              <InfoTile>
                <InfoIconWrap><FormatIcon /></InfoIconWrap>
                <InfoText>
                  <InfoLabel>업로드 가능 파일 형식</InfoLabel>
                  <InfoValue>모든 파일 형식</InfoValue>
                </InfoText>
              </InfoTile>
              <InfoTile>
                <InfoIconWrap><SizeIcon /></InfoIconWrap>
                <InfoText>
                  <InfoLabel>최대 파일 크기</InfoLabel>
                  <InfoValue>5GB / 개</InfoValue>
                </InfoText>
              </InfoTile>
              <InfoTile>
                <InfoIconWrap><StackIcon /></InfoIconWrap>
                <InfoText>
                  <InfoLabel>동시 업로드</InfoLabel>
                  <InfoValue>최대 10개 파일</InfoValue>
                </InfoText>
              </InfoTile>
            </InfoRow>

            <div>
              <SectionTitle>업로드 옵션</SectionTitle>
              <OptionList>
                <OptionRow>
                  <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                  동일 파일이 있을 경우 덮어쓰기
                </OptionRow>
                <OptionRow>
                  <input type="checkbox" checked={notifyDone} onChange={(e) => setNotifyDone(e.target.checked)} />
                  업로드 완료 후 알림 받기
                </OptionRow>
                <OptionRow>
                  <input type="checkbox" checked={openAfter} onChange={(e) => setOpenAfter(e.target.checked)} />
                  업로드 완료 후 폴더 열기
                </OptionRow>
              </OptionList>
            </div>

            {files.length > 0 && (
              <FileList>
                {isUploading && (
                  <>
                    <TotalProgressInfo>
                      <span>전체 진행률</span>
                      <span>{totalProgress}%</span>
                    </TotalProgressInfo>
                    <ProgressBarOuter style={{ width: '100%', marginBottom: '0.5rem' }}>
                      <ProgressBarInner pct={totalProgress} />
                    </ProgressBarOuter>
                  </>
                )}
                {files.map((file) => {
                  const style = getFileTypeStyle(file.name);
                  const pct = uploadProgress[file.name] || 0;
                  return (
                    <FileItem key={file.name}>
                      <FileIcon bg={style.bg} color={style.color}>{style.label}</FileIcon>
                      <FileMeta>
                        <FileNameText>{file.name}</FileNameText>
                        <FileSubText>{formatFileSize(file.size)}</FileSubText>
                      </FileMeta>
                      <ProgressBarOuter><ProgressBarInner pct={pct} /></ProgressBarOuter>
                      {!isUploading && (
                        <RemoveBtn onClick={() => removeFile(file.name)} aria-label="제거">
                          <XSvg />
                        </RemoveBtn>
                      )}
                    </FileItem>
                  );
                })}
              </FileList>
            )}

            <StartBtn
              onClick={handleUpload}
              disabled={isUploading || files.length === 0 || !selectedRemote}
            >
              {!selectedRemote ? '원격지를 선택하세요'
                : files.length === 0 ? '파일을 추가하세요'
                : isUploading ? `업로드 중... (${totalProgress}%)`
                : `업로드 시작 (${files.length}개)`}
            </StartBtn>
          </CardBody>
        </Card>

        {/* ==== 우측 사이드 카드들 ==== */}
        <SideStack>
          <Card>
            <CardHead><CardTitle>업로드 가이드</CardTitle></CardHead>
            <CardBody style={{ paddingTop: '0.4rem' }}>
              <GuideRow>
                <InfoCircleIcon />
                <GuideText>여러 파일을 한 번에 업로드할 수 있습니다.</GuideText>
              </GuideRow>
              <GuideRow>
                <InfoCircleIcon />
                <GuideText>대용량 파일은 업로드 시간이 오래 걸릴 수 있습니다.</GuideText>
              </GuideRow>
              <GuideRow>
                <BellSvg />
                <GuideText>업로드가 완료되면 알림으로 알려드립니다.</GuideText>
              </GuideRow>
            </CardBody>
          </Card>

          <Card>
            <CardHead>
              <CardTitle>최근 업로드</CardTitle>
              <MoreLink type="button">더보기 →</MoreLink>
            </CardHead>
            <CardBody style={{ paddingTop: '0.4rem' }}>
              {recent.length === 0 ? (
                <Empty>아직 업로드한 파일이 없습니다.</Empty>
              ) : (
                recent.map((f, i) => {
                  const style = getFileTypeStyle(f.name);
                  return (
                    <RecentRow key={`${f.name}-${i}`}>
                      <FileIcon bg={style.bg} color={style.color}>{style.label}</FileIcon>
                      <FileMeta>
                        <RecentName>{f.name}</RecentName>
                        <RecentSub>{formatFileSize(f.size)} · {f.date}</RecentSub>
                      </FileMeta>
                      <CheckBadge><CheckSvg /></CheckBadge>
                    </RecentRow>
                  );
                })
              )}
            </CardBody>
          </Card>
        </SideStack>
      </Layout>

      <FooterText>© ANTSNEST DEVELOPER CENTER Co., Ltd. All Rights Reserved.</FooterText>
    </Page>
  );
};

export default Upload;
